import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { SseService } from './SseService';

/**
 * Centralizes formatting of dates/times in a MetaApi broker's own local time
 * (tbl_metaapi_cuenta.broker_utc_offset_min), instead of the browser's
 * timezone. Used for both the market-hours countdown (opening/closing) and
 * operation/order execution timestamps across /broker.
 *
 * Implementation note: `Intl.DateTimeFormat`'s `timeZone` option only accepts
 * real IANA zone names ("UTC", "Etc/GMT-3", ...) — NOT arbitrary offset
 * strings like "+03:00" (throws RangeError; confirmed does not work, this
 * isn't a stylistic choice). Since broker offsets can be arbitrary minutes
 * (half-hour brokers exist) and there's no fixed-offset IANA zone for those,
 * every method here shifts the instant by the offset in plain epoch-ms math
 * and reads/writes it via the UTC getters/`Date.UTC` — which is always valid
 * regardless of the offset's granularity — instead of asking Intl to resolve
 * a timezone.
 */
/** Respuesta de horasdemercadoporidnectum. */
export interface MarketSchedule {
  iso: string;
  nombre: string;
  tipo: number;
  sectype: string;
  opening_hour: string;
  closing_hour: string;
  timezone: string;
  is_holiday?: boolean;
  mtServer?: string;
}

export type EstadoMercado = 'ABIERTO' | 'CERRADO' | 'DESCONOCIDO';

export interface EstadoMercadoInfo {
  estado: EstadoMercado;
  /** Próximo evento para la cuenta atrás; null si es feriado o el horario es desconocido. */
  evento: { tipo: 'Abre' | 'Cierra', fecha: Date } | null;
  feriado: boolean;
}

const DIA_MS = 24 * 60 * 60 * 1000;

@Injectable({
  providedIn: 'root'
})
export class BrokerTimeService {

  private static readonly SCHEDULE_TTL_MS = 10 * 60 * 1000;
  private scheduleCache = new Map<string, { schedule: MarketSchedule | null, at: number }>();

  constructor(private sseService: SseService, private cookieService: CookieService) { }

  /** Reads the already-measured offset for an account; null if unknown/unmeasured. */
  getOffsetMinutes(accountId: string): Observable<number | null> {
    if (!accountId) return of(null);
    const licencia = this.cookieService.get('auth_token');
    return this.sseService.apiGetWithAuth(`metaapi/broker-offset/${accountId}`, licencia).pipe(
      map((data: any) => (typeof data?.brokerUtcOffsetMin === 'number' ? data.brokerUtcOffsetMin : null)),
      catchError(() => of(null))
    );
  }

  /** "+HH:mm" / "-HH:mm" (or "Z" for 0) — display label only, NOT usable as Intl.DateTimeFormat's timeZone. */
  offsetMinutesToTimeZone(offsetMin: number | null | undefined): string | undefined {
    if (offsetMin == null || !Number.isFinite(offsetMin)) return undefined;
    if (offsetMin === 0) return 'Z';
    const sign = offsetMin < 0 ? '-' : '+';
    const abs = Math.abs(offsetMin);
    const hh = Math.floor(abs / 60).toString().padStart(2, '0');
    const mm = (abs % 60).toString().padStart(2, '0');
    return `${sign}${hh}:${mm}`;
  }

  /** Parses "+HH:mm" / "-HH:mm" / "Z" back into minutes. 0 for anything unrecognized. */
  private parseOffsetToMinutes(offset: string | null | undefined): number {
    if (!offset || offset === 'Z' || offset.toUpperCase() === 'UTC') return 0;
    const m = /^([+-])(\d{2}):?(\d{2})$/.exec(offset.trim());
    if (!m) return 0;
    const sign = m[1] === '-' ? -1 : 1;
    return sign * (parseInt(m[2], 10) * 60 + parseInt(m[3], 10));
  }

  /** date shifted so its UTC getters read as the broker's own wall-clock numbers. */
  private toBrokerWallClock(date: Date, offsetMin: number): Date {
    return new Date(date.getTime() + offsetMin * 60000);
  }

  /**
   * Epoch ms del próximo límite múltiplo de `stepHours` en la hora del broker
   * (ej. stepHours=4 → 0,4,8,12,16,20h; stepHours=24 → próxima medianoche).
   * Requiere offsetMin conocido — el llamador decide el fallback cuando es null.
   */
  getNextBrokerBoundaryMs(baseDate: Date, stepHours: number, offsetMin: number): number {
    const wallClock = this.toBrokerWallClock(baseDate, offsetMin);
    const hour = wallClock.getUTCHours();
    let nextHour = Math.floor(hour / stepHours) * stepHours + stepHours;
    let extraDayMs = 0;
    if (nextHour >= 24) { nextHour -= 24; extraDayMs = 24 * 60 * 60 * 1000; }
    const boundaryAsWallClockUtc = Date.UTC(
      wallClock.getUTCFullYear(), wallClock.getUTCMonth(), wallClock.getUTCDate(), nextHour, 0, 0
    );
    return boundaryAsWallClockUtc - offsetMin * 60000 + extraDayMs;
  }

  /** HH:mm:ss in the broker's timezone; falls back to the browser's timezone if offsetMin is null. */
  formatTime(date: Date, offsetMin: number | null | undefined): string {
    if (!date || Number.isNaN(date.getTime())) return '';
    if (offsetMin == null || !Number.isFinite(offsetMin)) {
      return new Intl.DateTimeFormat('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(date);
    }
    const wallClock = this.toBrokerWallClock(date, offsetMin);
    return new Intl.DateTimeFormat('es-ES', {
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: 'UTC'
    }).format(wallClock);
  }

  /**
   * HH:mm:ss en una zona IANA (p.ej. America/New_York, la del exchange en exchange_market_hours).
   * Intl sí acepta nombres IANA y aplica el horario de verano; '' si la zona no es válida.
   */
  formatTimeInZone(date: Date, timeZone: string): string {
    if (!date || Number.isNaN(date.getTime()) || !timeZone) return '';
    try {
      return new Intl.DateTimeFormat('es-ES', {
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone
      }).format(date);
    } catch {
      return '';
    }
  }

  /** dd/MM/yyyy in the broker's timezone; falls back to the browser's timezone if offsetMin is null. */
  formatDate(date: Date, offsetMin: number | null | undefined): string {
    if (!date || Number.isNaN(date.getTime())) return '';
    if (offsetMin == null || !Number.isFinite(offsetMin)) {
      return new Intl.DateTimeFormat('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
    }
    const wallClock = this.toBrokerWallClock(date, offsetMin);
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC'
    }).format(wallClock);
  }

  /** dd/MM/yyyy HH:mm:ss in the broker's timezone; falls back to the browser's timezone if offsetMin is null. */
  formatDateTime(date: Date, offsetMin: number | null | undefined): string {
    if (!date || Number.isNaN(date.getTime())) return '';
    if (offsetMin == null || !Number.isFinite(offsetMin)) {
      return new Intl.DateTimeFormat('es-ES', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
      }).format(date);
    }
    const wallClock = this.toBrokerWallClock(date, offsetMin);
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: 'UTC'
    }).format(wallClock);
  }

  // ── Market-hours countdown helpers (opeentrada-dialog, grafica-modelo) ────
  // timeZone here is the "+HH:mm" offset string already returned by
  // horasdemercadoporidnectum (schedule.timezone) — parsed back to minutes
  // internally, never handed to Intl.DateTimeFormat as a timeZone.

  /** Parses a "h:mm a +HH:mm" spec (as returned by horasdemercadoporidnectum) into a Date anchored to `reference`'s day in that timezone. */
  createMarketDate(timeSpec: string, reference: Date, timeZone: string): Date | null {
    if (!timeSpec) return null;

    const parts = timeSpec.trim().split(' ');
    if (parts.length < 3) return null;

    const timePart = parts[0];
    const meridian = parts[1]?.toUpperCase();
    const [hoursStr, minutesStr] = timePart.split(':');
    let hours = Number(hoursStr);
    const minutes = Number(minutesStr);

    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;

    if (meridian === 'PM' && hours < 12) {
      hours += 12;
    } else if (meridian === 'AM' && hours === 12) {
      hours = 0;
    }

    const isOffsetSpec = (s: string | undefined) => !!s && /^(Z|UTC|[+-]\d{2}:?\d{2})$/i.test(s.trim());
    // timeZone puede ser un offset (MetaApi) o un nombre IANA (mercados no-MetaApi, p.ej. America/New_York);
    // en ese caso el offset correcto viene en el propio timeSpec ("9:30 AM -04:00").
    const offsetMin = this.parseOffsetToMinutes(isOffsetSpec(timeZone) ? timeZone : parts[2]);

    let year: number, month: number, day: number;
    let ianaDay: number[] | null = null;
    if (timeZone && !isOffsetSpec(timeZone)) {
      try {
        const ymd = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(reference);
        ianaDay = ymd.split('-').map(Number);
      } catch { /* zona inválida: se usa el offset */ }
    }
    if (ianaDay && ianaDay.length === 3 && ianaDay.every(Number.isFinite)) {
      [year, month, day] = [ianaDay[0], ianaDay[1] - 1, ianaDay[2]];
    } else {
      const wallClockRef = this.toBrokerWallClock(reference, offsetMin);
      [year, month, day] = [wallClockRef.getUTCFullYear(), wallClockRef.getUTCMonth(), wallClockRef.getUTCDate()];
    }
    const wallClockUtcMs = Date.UTC(year, month, day, hours, minutes, 0);
    const result = new Date(wallClockUtcMs - offsetMin * 60000);
    return Number.isFinite(result.getTime()) ? result : null;
  }

  /**
   * Horario de mercado de un activo (horasdemercadoporidnectum), cacheado 10 min por
   * idNectum|metaapi|idmercado: la cola automática de /broker lo consulta cada pocos segundos.
   * null si el backend no conoce el horario (MetaApi sin offset medido, iso sin exchange...).
   */
  getMarketSchedule(idNectum: number | undefined, wallet: { tipoBroker?: string, idmercado?: number } | null | undefined): Observable<MarketSchedule | null> {
    if (!idNectum) return of(null);
    const metaapi = wallet?.tipoBroker === 'METAAPI';
    const idmercado = wallet?.idmercado;
    const key = `${idNectum}|${metaapi}|${idmercado ?? ''}`;
    const cached = this.scheduleCache.get(key);
    if (cached && Date.now() - cached.at < BrokerTimeService.SCHEDULE_TTL_MS) return of(cached.schedule);

    const idmercadoParam = idmercado ? `&idmercado=${idmercado}` : '';
    return this.sseService.apiGetNew(`horasdemercadoporidnectum?idnectum=${idNectum}&metaapi=${metaapi}${idmercadoParam}`).pipe(
      map((data: any) => {
        const schedule: MarketSchedule | null = Array.isArray(data)
          ? (data.length ? data[0] : null)
          : (data && typeof data === 'object' ? data : null);
        this.scheduleCache.set(key, { schedule, at: Date.now() });
        return schedule;
      }),
      catchError(() => of(null))
    );
  }

  /**
   * Estado del mercado en `now` y el próximo evento (apertura o cierre) para la cuenta atrás.
   * DESCONOCIDO cuando no hay horario utilizable: quien lo consuma decide (la cola automática
   * deja pasar la orden, igual que MercadoRelojService.mercadoEnVentana en el backend).
   */
  estadoMercado(schedule: MarketSchedule | null | undefined, now: Date = new Date()): EstadoMercadoInfo {
    if (!schedule) return { estado: 'DESCONOCIDO', evento: null, feriado: false };
    if (schedule.is_holiday) return { estado: 'CERRADO', evento: null, feriado: true };

    const open = this.createMarketDate(schedule.opening_hour, now, schedule.timezone);
    let close = this.createMarketDate(schedule.closing_hour, now, schedule.timezone);
    if (!open || !close) return { estado: 'DESCONOCIDO', evento: null, feriado: false };
    if (close.getTime() <= open.getTime()) close = new Date(close.getTime() + DIA_MS);

    // MetaApi ya marca is_holiday los días sin sesiones; los horarios de exchange (zona IANA)
    // no traen el fin de semana, así que se comprueba aquí.
    const finDeSemana = this.esFinDeSemana(now, schedule.timezone);
    const t = now.getTime();
    if (!finDeSemana && t >= open.getTime() && t < close.getTime()) {
      return { estado: 'ABIERTO', evento: { tipo: 'Cierra', fecha: close }, feriado: false };
    }

    let nextOpen = !finDeSemana && t < open.getTime() ? open : new Date(open.getTime() + DIA_MS);
    for (let i = 0; i < 7 && this.esFinDeSemana(nextOpen, schedule.timezone); i++) {
      nextOpen = new Date(nextOpen.getTime() + DIA_MS);
    }
    return { estado: 'CERRADO', evento: { tipo: 'Abre', fecha: nextOpen }, feriado: false };
  }

  /** Sábado/domingo en la zona IANA del mercado; false si timeZone es un offset (MetaApi) o inválida. */
  private esFinDeSemana(date: Date, timeZone: string | undefined): boolean {
    if (!timeZone || /^(Z|UTC|[+-]\d{2}:?\d{2})$/i.test(timeZone.trim())) return false;
    try {
      const dia = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short' }).format(date);
      return dia === 'Sat' || dia === 'Sun';
    } catch {
      return false;
    }
  }

  formatEventTime(targetDate: Date, timeZone: string): string {
    const offsetMin = this.parseOffsetToMinutes(timeZone);
    const reference = new Date();
    const wallClockTarget = this.toBrokerWallClock(targetDate, offsetMin);
    const wallClockRef = this.toBrokerWallClock(reference, offsetMin);

    const sameDay = wallClockTarget.getUTCFullYear() === wallClockRef.getUTCFullYear()
      && wallClockTarget.getUTCMonth() === wallClockRef.getUTCMonth()
      && wallClockTarget.getUTCDate() === wallClockRef.getUTCDate();

    if (sameDay) {
      return new Intl.DateTimeFormat('es-ES', {
        hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC'
      }).format(wallClockTarget);
    }

    return new Intl.DateTimeFormat('es-ES', {
      weekday: 'short', day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC'
    }).format(wallClockTarget);
  }

  formatDuration(ms: number): string {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }
}
